import { Clock, AlertCircle, CircleAlert, CircleCheckBig } from "lucide-react";
import { Badge } from "./badge";
import { cn } from "@/lib/utils";

export type DocumentStatus =
    | "QUEUED"
    | "PROCESSING"
    | "SUBMITTED"
    | "FAILED"
    | "COMPLETED"
    | "APPROVED";

export interface StatusBadgeProps {
    status: DocumentStatus;
    className?: string;
}

export function StatusBadge({ status, className }: StatusBadgeProps) {
    const getStatusConfig = (status: DocumentStatus) => {
        switch (status) {
            case "QUEUED":
            case "PROCESSING":
            case "SUBMITTED":
                return {
                    icon: Clock,
                    className: "bg-gray-200 text-gray-700 border-gray-500",
                    label: status,
                };
            case "FAILED":
                return {
                    icon: AlertCircle,
                    className: "bg-red-100 text-red-800 border-red-200",
                    label: "Failed",
                };
            // Completed processing, now waiting for approval
            case "COMPLETED":
                return {
                    icon: CircleAlert,
                    className:
                        "bg-yellow-100 text-orange-900 border-orange-100",
                    label: "Review",
                };
            case "APPROVED":
                return {
                    icon: CircleCheckBig,
                    className: "bg-blue-700 text-white border-blue-800",
                    label: "Approved",
                };
            default:
                return {
                    icon: Clock,
                    className: "bg-gray-200 text-gray-700 border-gray-300",
                    label: status || "Unknown",
                };
        }
    };

    const config = getStatusConfig(status);
    const Icon = config.icon;

    return (
        <Badge
            variant="outline"
            className={cn(
                config.className,
                className,
                "tracking-tight -border-none rounded-md font-medium text-xs",
            )}
        >
            <Icon className="mr-1 h-3 w-3" />
            {config.label}
        </Badge>
    );
}
